/**
 * Incident correlation primitives — T38.
 *
 * The user-visible half of an incident (the `XXXX-XXXX-XXXX` code on the 500
 * surface) and the server-side half (the `X-Request-Id` response header, the
 * observability event) must be **the same value**, or the code a member reads
 * out to support correlates with nothing. This module is the single place that
 * value is derived, so every consumer agrees by construction.
 *
 * Pure by design (`app/lib` rule): no IO, no `Env`, no react-router import.
 * `crypto.getRandomValues` is a Web API present in both the Workers runtime and
 * the browser, which is what lets the same function run server-side (from
 * `cf-ray`) and client-side (the fallback the boundary uses when root's loader
 * is the thing that failed).
 */

/**
 * Crockford-style **alphabet** — the symbol set only, not Crockford's encoding.
 * Per the T104 view contract: no `I`/`O`/`0`/`1`, so the code survives being read
 * aloud on the phone or copied off a screenshot. Exactly 32 symbols → 5 bits each.
 *
 * The id is an opaque label, not a decodable number: `encodeGroup` consumes bits
 * low-order-first, so the characters do not spell out the seed in any base-32
 * reading. Nothing should try to invert it — correlate by string equality.
 */
export const INCIDENT_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

const GROUPS = 3;
const GROUP_LEN = 4;
/** `XXXX-XXXX-XXXX` — 12 symbols in 3 groups. Internal: consumers match on `INCIDENT_ID_RE`. */
const INCIDENT_ID_LENGTH = GROUPS * GROUP_LEN;

/** Matches a well-formed incident id — used to pass an upstream one through untouched. */
export const INCIDENT_ID_RE = new RegExp(
  `^[${INCIDENT_ALPHABET}]{${GROUP_LEN}}(-[${INCIDENT_ALPHABET}]{${GROUP_LEN}}){${GROUPS - 1}}$`,
);

/** FNV-1a (32-bit), seeded. Not a security primitive — a stable, cheap mixer. */
function fnv1a(input: string, seed: number): number {
  let h = seed >>> 0;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i) & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

function encodeGroup(bits: number): string {
  let h = bits >>> 0;
  let out = "";
  for (let i = 0; i < GROUP_LEN; i += 1) {
    out += INCIDENT_ALPHABET[h & 31];
    h >>>= 5;
  }
  return out;
}

/**
 * Deterministically project an arbitrary upstream id (a `cf-ray`, a proxy's
 * `X-Request-Id`) onto the display format.
 *
 * Deterministic on purpose: the same request must produce the same code in the
 * response header, in the rendered surface and in the observability event, and
 * SSR must reach the same answer the client would. Three independently seeded
 * passes because one FNV-1a pass only yields 32 bits and the format wants 60.
 */
export function formatIncidentId(seed: string): string {
  const groups: string[] = [];
  for (let g = 0; g < GROUPS; g += 1) {
    // Golden-ratio stride per group so the passes decorrelate.
    groups.push(encodeGroup(fnv1a(seed, (0x811c9dc5 + Math.imul(g, 0x9e3779b1)) >>> 0)));
  }
  return groups.join("-");
}

/**
 * Fresh random incident id. The client-side fallback: used only when nothing
 * upstream supplied one (root's own loader failed, so no loader data reached
 * the boundary). Correlates with the browser-side event, not with a server log
 * — which is the honest outcome when the server never got far enough to log.
 */
export function randomIncidentId(): string {
  const bytes = new Uint8Array(INCIDENT_ID_LENGTH);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < INCIDENT_ID_LENGTH; i += 1) {
    if (i > 0 && i % GROUP_LEN === 0) out += "-";
    out += INCIDENT_ALPHABET[bytes[i]! & 31];
  }
  return out;
}

/**
 * Derive the incident id for an inbound request.
 *
 * Precedence: an upstream `X-Request-Id` already in our format passes through
 * verbatim; any other `X-Request-Id` or Cloudflare's `cf-ray` is projected onto
 * the format; absent both (local `vite dev`, unit tests) we mint a random one.
 *
 * `cf-ray` is set by Cloudflare on every inbound request — that is why this
 * ticket needs no `wrangler.toml` change to get a request id
 * (precedent: `app/services/audit-log.server.ts` already reads it).
 */
export function incidentIdFromHeaders(headers: Headers): string {
  const upstream = headers.get("x-request-id")?.trim();
  if (upstream) {
    return INCIDENT_ID_RE.test(upstream) ? upstream : formatIncidentId(upstream);
  }
  const ray = headers.get("cf-ray")?.trim();
  return ray ? formatIncidentId(ray) : randomIncidentId();
}

/** `YYYY-MM-DD · HH:MM UTC` — the exact string the T104 footer renders. */
export function formatIncidentTimestamp(date: Date): string {
  const iso = date.toISOString();
  return `${iso.slice(0, 10)} · ${iso.slice(11, 16)} UTC`;
}

/** Upper bound on a stored referrer, so a pathological URL cannot bloat an event. */
const REFERRER_MAX = 256;

/**
 * Reduce a referrer to `origin + pathname`.
 *
 * The query string and fragment are dropped, never logged: a referrer is
 * attacker- and third-party-controlled and routinely carries tokens, emails and
 * session ids in its query. The spec's "sin datos personales" is enforced here
 * rather than trusted to each call site.
 */
export function safeReferrer(raw: string | null | undefined): string {
  if (!raw) return "";
  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    return `${url.origin}${url.pathname}`.slice(0, REFERRER_MAX);
  } catch {
    return "";
  }
}

/**
 * Headers for a 503, prepared for T56 (maintenance / status page).
 *
 * T56 owns the maintenance *surface*; the plumbing it needs is only this — a
 * `Retry-After` alongside the incident header, so a throw of
 * `data(payload, { status: 503, headers: retryAfterHeaders(n, id) })` lands on
 * the 5xx branch of root's `ErrorBoundary` with correlation intact and nothing
 * to rewrite here.
 *
 * @param seconds Delta-seconds for `Retry-After`; clamped to a non-negative integer.
 */
export function retryAfterHeaders(seconds: number, incidentId?: string): Record<string, string> {
  const safe = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
  const headers: Record<string, string> = { "Retry-After": String(safe) };
  if (incidentId) headers["X-Request-Id"] = incidentId;
  return headers;
}
